import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { StatusCard } from "./kimkim/StatusCard";
import { Filters } from "./kimkim/Filters";
import { LeadsTable } from "./kimkim/LeadsTable";
import { Comparison } from "./kimkim/Comparison";
import { AgentMapModal } from "./kimkim/AgentMapModal";
import { BudgetTiers } from "./kimkim/BudgetTiers";
import { Report, PERIODS, periodRange } from "./kimkim/Report";
import { Patterns } from "./kimkim/Patterns";
import { ExportBar } from "./kimkim/report/ExportBar";
import { AccessControl } from "./kimkim/report/AccessControl";
import { MigrationCard } from "./kimkim/MigrationCard";
import { BUCKETS, EXTRA_FILTERS, FUNNEL_SHORT, LOSS_REASON_LABELS, PRICE_VS, THEME_LABELS, WEEKDAYS } from "./kimkim/labels";

const BASE_KEYS = ["agent", "date_from", "date_to", "destination", "tier", "budget_band", "lead_time_band", "response_band", "q"];
const FILTER_KEYS = [...BASE_KEYS, ...Object.keys(EXTRA_FILTERS)];
const VIEWS = [{ key: "list", label: "Listado" }, { key: "report", label: "Informe" }];
const VIEW_KEYS = { list: "list", report: "report", analysis: "report" };

function ReportControls({ params, write, agents, queryParams }) {
  const period = params.get("period") || "2026";
  const agent = params.get("agent") || "";
  const set = (changes) => { const next = new URLSearchParams(params); Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k))); write(next); };
  const setPeriod = (p) => set(p === "custom" ? { period: p } : { period: p, ...periodRange(p) });
  const sel = "border border-clay-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-clay-900";
  return (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-3" data-testid="kk-report-controls">
      <label className="block"><div className="smallcaps mb-1">Periodo</div>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className={sel} data-testid="kk-period-select">{PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}</select>
      </label>
      {period === "custom" && (
        <div className="flex items-end gap-2" data-testid="kk-period-custom">
          <label className="block"><div className="smallcaps mb-1">Desde</div><input type="date" value={params.get("date_from") || ""} onChange={(e) => set({ date_from: e.target.value })} className={sel} data-testid="kk-period-from"/></label>
          <label className="block"><div className="smallcaps mb-1">Hasta</div><input type="date" value={params.get("date_to") || ""} onChange={(e) => set({ date_to: e.target.value })} className={sel} data-testid="kk-period-to"/></label>
        </div>
      )}
      <label className="block"><div className="smallcaps mb-1">Agente</div>
        <select value={agent} onChange={(e) => set({ agent: e.target.value })} className={sel} data-testid="kk-report-agent-select">
          <option value="">Todo el equipo</option>
          {agents.map((a) => <option key={a.email} value={a.email}>{a.name}</option>)}
        </select>
      </label>
      <div className="ml-auto"><ExportBar queryParams={queryParams}/></div>
    </div>
  );
}

function extraLabel(k, v) {
  if (k === "funnel_step") return FUNNEL_SHORT[v] || v;
  if (k === "loss_reason") return LOSS_REASON_LABELS[v] || v;
  if (k === "theme") return THEME_LABELS[v] || v;
  if (k === "price_vs") return PRICE_VS[v] || v;
  if (k === "weekday") return WEEKDAYS[Number(v)] || v;
  if (k === "cohort") return "solo leads desde 2026";
  return v;
}

export default function KimKimLeads() {
  const [params, setParams] = useSearchParams();
  const bucket = params.get("bucket") || "not_sold";
  const rawView = VIEW_KEYS[params.get("view")] || "list";
  const reportTab = params.get("tab") === "patterns" ? "patterns" : "report";
  const filters = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || undefined])), [params]);
  const [status, setStatus] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [access, setAccess] = useState(null);  // who may open the Informe (null = unknown yet)
  const canReport = access?.can_view === true;
  const view = rawView === "report" && access && !canReport ? "list" : rawView;

  const loadStatus = useCallback(() => api.get("/kimkim/status").then(({ data }) => setStatus(data)).catch(() => {}), []);
  const queryParams = useMemo(() => {
    const p = {};
    FILTER_KEYS.forEach((k) => { if (filters[k]) p[k] = filters[k]; });
    if (p.agent === "__none__") p.agent = "";
    if (view === "report" && !p.date_from) Object.assign(p, periodRange(params.get("period") || "2026"));
    return p;
  }, [filters, view, params]);

  const loadData = useCallback(async () => {
    if (view !== "list") return;
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/kimkim/leads", { params: { ...queryParams, bucket, limit: 5000 } }),
        api.get("/kimkim/stats", { params: queryParams }),
      ]);
      setLeads(l.data.leads); setStats(s.data);
    } catch (e) { toast.error(e?.response?.data?.detail ? String(e.response.data.detail) : "No se pudieron cargar los leads"); }
    finally { setLoading(false); }
  }, [queryParams, bucket, view]);

  useEffect(() => {
    loadStatus();
    api.get("/kimkim/agent-map").then(({ data }) => setAgents(data.candidates || [])).catch(() => {});
    api.get("/kimkim/report/access").then(({ data }) => setAccess(data)).catch(() => setAccess({ can_view: false, can_manage: false, viewers: [], candidates: [] }));
  }, [loadStatus]);
  useEffect(() => { loadData(); }, [loadData]);
  const jobDone = status?.job?.status;
  useEffect(() => { if (jobDone === "done") loadData(); }, [jobDone, loadData]);

  const write = (next) => setParams(next, { replace: true });
  const setFilters = (f) => {
    const next = new URLSearchParams();
    next.set("bucket", bucket); next.set("view", view);
    FILTER_KEYS.forEach((k) => { if (f[k]) next.set(k, f[k]); });
    write(next);
  };
  const setBucket = (b) => { const next = new URLSearchParams(params); next.set("bucket", b); write(next); };
  const setView = (v) => { const next = new URLSearchParams(params); next.set("view", v); write(next); };
  // from the analysis: open the list filtered by whatever number was clicked
  const drill = (extra) => {
    const next = new URLSearchParams(params);
    next.set("view", "list");
    next.set("bucket", extra.bucket || "all");
    if (extra.all_time) next.delete("cohort"); else next.set("cohort", "1");  // analysis counts use the 2026 cohort
    Object.entries(extra).forEach(([k, v]) => { if (!["bucket", "all_time"].includes(k) && v !== undefined && v !== null) next.set(k, String(v)); });
    write(next);
    window.scrollTo({ top: 0 });
  };
  const activeExtras = Object.keys(EXTRA_FILTERS).filter((k) => filters[k]);

  return (
    <div className="px-8 py-8 max-w-[1500px]" data-testid="kimkim-page">
      <div className="mb-6">
        <div className="smallcaps">Administración</div>
        <h1 className="font-serif text-5xl leading-none mt-3">Leads KimKim</h1>
        <p className="text-sm text-clay-700 mt-3 max-w-3xl">Todos los viajes de KimKim con su conversación y su agente, ordenados en categorías estables, para ver dónde se gana y dónde se pierde, por agente y para la empresa.</p>
      </div>

      <div className="space-y-6">
        <StatusCard status={status} onRefresh={loadStatus} onOpenAgentMap={() => setShowMap(true)}/>
        {access?.can_manage && status && <MigrationCard totalLeads={status.counts.total} onDone={() => { loadStatus(); loadData(); }}/>}
        <div className="flex items-center gap-1 border-b border-clay-300" data-testid="kimkim-views">
          {VIEWS.filter((v) => v.key !== "report" || canReport).map((v) => (
            <button key={v.key} onClick={() => setView(v.key)} data-testid={`kimkim-view-${v.key}`}
              className={`px-4 py-2 text-sm border-b-2 -mb-px ${view === v.key ? "border-clay-900 text-clay-900 font-semibold" : "border-transparent text-clay-600 hover:text-clay-900"}`}>{v.label}</button>
          ))}
        </div>
        {view === "report" ? (
          <>
            <ReportControls params={params} write={write} agents={agents} queryParams={queryParams}/>
            <AccessControl access={access} onChange={setAccess}/>
            <div className="flex items-center gap-1 border-b border-clay-200" data-testid="kk-report-tabs">
              {[["report", "Informe"], ["patterns", "Patrones"]].map(([k, label]) => (
                <button key={k} type="button" onClick={() => { const next = new URLSearchParams(params); if (k === "report") next.delete("tab"); else next.set("tab", k); write(next); }}
                  data-testid={`kk-report-tab-${k}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider border-b-2 -mb-px ${reportTab === k ? "border-terracotta text-clay-900 font-semibold" : "border-transparent text-clay-500 hover:text-clay-900"}`}>{label}</button>
              ))}
            </div>
            {reportTab === "patterns" ? <Patterns queryParams={queryParams}/> : <Report queryParams={queryParams} drill={drill}/>}
          </>
        ) : (
          <>
        <Filters value={filters} onChange={setFilters} agents={agents} destinations={stats?.destinations || []}/>
        {activeExtras.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid="kimkim-extra-filters">
            <span className="text-clay-500">Filtros del análisis:</span>
            {activeExtras.map((k) => (
              <span key={k} className="inline-flex items-center gap-1 border border-terracotta/40 bg-terracotta/10 px-2 py-0.5" data-testid={`kimkim-extra-filter-${k}`}>
                {EXTRA_FILTERS[k]}: {extraLabel(k, filters[k])}
                <button onClick={() => setFilters({ ...filters, [k]: undefined })} aria-label="Quitar filtro" data-testid={`kimkim-extra-filter-${k}-remove`}><X size={11}/></button>
              </span>
            ))}
          </div>
        )}
            <BudgetTiers/>
            <Comparison stats={stats}/>
            <div>
              <div className="flex items-center gap-1 border-b border-clay-300 mb-4" data-testid="kimkim-tabs">
                {BUCKETS.map((b) => {
                  const n = stats ? (b.key === "all" ? stats.company.leads : stats.company[b.key]) : null;
                  return (
                    <button key={b.key} onClick={() => setBucket(b.key)} data-testid={`kimkim-tab-${b.key}`}
                      className={`px-4 py-2 text-sm border-b-2 -mb-px ${bucket === b.key ? "border-terracotta text-clay-900 font-semibold" : "border-transparent text-clay-600 hover:text-clay-900"}`}>
                      {b.label}{n != null && <span className="ml-1.5 text-[11px] text-clay-500 tabular">{n}</span>}
                    </button>
                  );
                })}
              </div>
              <LeadsTable leads={leads} loading={loading}/>
            </div>
          </>
        )}
      </div>
      {showMap && <AgentMapModal onClose={() => setShowMap(false)} onSaved={() => { loadStatus(); loadData(); }}/>}
    </div>
  );
}
